import { registerIndicator, type KLineData } from 'klinecharts'
import { getIndicatorDef } from '@edge/charts-indicators'
import { MA_CLONE_CALCS } from './maBandIndicators'
import { withTimeframe } from './mtfCalc'
import { type IndicatorTemplateParam } from './registrationUtils'

/**
 * klinecharts keys indicators by `name` within a pane and silently rejects a
 * second instance of the same name ("Duplicate indicators."). That makes two
 * EMAs (or MAs/SMAs) collapse into one — editing one edits "the" EMA. To let
 * them coexist as independent legend rows, each extra instance is registered
 * under a unique name (`EMA#2`, `EMA#3`, …) backed by an app-owned clone of the
 * built-in's math. The duplicated set is the MA family plus BOLL and ATR —
 * the indicators users actually stack, with formulas that are trivial to own
 * exactly (mirrored from klinecharts' built-in templates).
 *
 * The clone set doubles as the MULTI-TIMEFRAME set: every clone calc is
 * registered wrapped in `withTimeframe`, so an instance whose extendData
 * carries a `timeframe` computes over higher-timeframe bars (and is a pure
 * passthrough otherwise): clock buckets for 5m to 4H, and for 1D / 1W the
 * daily series ChartManager attaches a reader for (see mtfSession). Built-in
 * templates cannot do this, since klinecharts does not export their calcs,
 * which is why an MTF instance must always route through a clone.
 */

const DUPLICABLE = new Set(['MA', 'EMA', 'SMA', 'BOLL', 'ATR', ...Object.keys(MA_CLONE_CALCS)])

export function isDuplicableIndicator(name: string): boolean {
  return DUPLICABLE.has(name)
}

/** Indicators whose settings offer a Timeframe select — exactly the clone set,
 *  since MTF requires the app-owned (wrappable) math. */
export function isMtfEligibleIndicator(name: string): boolean {
  return DUPLICABLE.has(name)
}

/** The catalog name behind an instance alias (`MA#2` -> `MA`). */
export function baseIndicatorName(instanceName: string): string {
  const hash = instanceName.indexOf('#')
  return hash === -1 ? instanceName : instanceName.slice(0, hash)
}

/** First free instance name for `baseName`, avoiding everything in `used`. */
export function allocateInstanceName(baseName: string, used: Set<string>): string {
  if (!used.has(baseName)) return baseName
  for (let n = 2; ; n++) {
    const candidate = `${baseName}#${n}`
    if (!used.has(candidate)) return candidate
  }
}

type CalcInput = { calcParams?: number[] }

export function maCalc(key: string) {
  return (dataList: KLineData[], indicator: CalcInput): Record<string, number>[] => {
    const period = Number(indicator.calcParams?.[0] ?? 20)
    let sum = 0
    return dataList.map((k, i) => {
      sum += k.close
      const out: Record<string, number> = {}
      if (i >= period - 1) {
        out[key] = sum / period
        sum -= dataList[i - (period - 1)].close
      }
      return out
    })
  }
}

export function emaCalc(key: string) {
  return (dataList: KLineData[], indicator: CalcInput): Record<string, number>[] => {
    const period = Number(indicator.calcParams?.[0] ?? 20)
    let closeSum = 0
    let ema = 0
    return dataList.map((k, i) => {
      const close = k.close
      closeSum += close
      const out: Record<string, number> = {}
      if (i >= period - 1) {
        ema = i > period - 1 ? (2 * close + (period - 1) * ema) / (period + 1) : closeSum / period
        out[key] = ema
      }
      return out
    })
  }
}

export function smaCalc(key: string) {
  return (dataList: KLineData[], indicator: CalcInput): Record<string, number>[] => {
    const period = Number(indicator.calcParams?.[0] ?? 20)
    const weight = Number(indicator.calcParams?.[1] ?? 1)
    let closeSum = 0
    let sma = 0
    return dataList.map((k, i) => {
      const close = k.close
      closeSum += close
      const out: Record<string, number> = {}
      if (i >= period - 1) {
        sma =
          i > period - 1
            ? (close * weight + sma * (period - weight + 1)) / (period + 1)
            : closeSum / period
        out[key] = sma
      }
      return out
    })
  }
}

/**
 * Wilder ATR: TR smoothed by `(prev·(n−1)+tr)/n`, seeded with the mean of the
 * first `n` true ranges (first bar's TR is its plain range — no prior close).
 */
export function atrCalc(key: string) {
  return (dataList: KLineData[], indicator: CalcInput): Record<string, number>[] => {
    const period = Math.max(1, Number(indicator.calcParams?.[0] ?? 14))
    let trSum = 0
    let atr = 0
    return dataList.map((k, i) => {
      const prevClose = i > 0 ? dataList[i - 1].close : k.close
      const tr = Math.max(k.high - k.low, Math.abs(k.high - prevClose), Math.abs(k.low - prevClose))
      const out: Record<string, number> = {}
      if (i < period) {
        trSum += tr
        if (i === period - 1) {
          atr = trSum / period
          out[key] = atr
        }
      } else {
        atr = (atr * (period - 1) + tr) / period
        out[key] = atr
      }
      return out
    })
  }
}

/**
 * Bollinger Bands: SMA midline ± multiplier × population standard deviation
 * over the same window (klinecharts' convention — σ divides by n, not n−1).
 * Keys arrive in catalog figure order: upper, middle, lower.
 */
export function bollCalc(keys: string[]) {
  const [upKey, midKey, dnKey] = keys
  return (dataList: KLineData[], indicator: CalcInput): Record<string, number>[] => {
    const period = Math.max(1, Number(indicator.calcParams?.[0] ?? 20))
    const mult = Number(indicator.calcParams?.[1] ?? 2)
    let sum = 0
    return dataList.map((k, i) => {
      sum += k.close
      const out: Record<string, number> = {}
      if (i >= period - 1) {
        const mean = sum / period
        let varSum = 0
        for (let j = i - (period - 1); j <= i; j++) {
          const d = dataList[j].close - mean
          varSum += d * d
        }
        const dev = Math.sqrt(varSum / period)
        out[upKey] = mean + mult * dev
        out[midKey] = mean
        out[dnKey] = mean - mult * dev
        sum -= dataList[i - (period - 1)].close
      }
      return out
    })
  }
}

const CALC: Record<string, (key: string) => unknown> = {
  MA: maCalc,
  EMA: emaCalc,
  SMA: smaCalc,
  ATR: atrCalc,
  // Extended MA family (WMA, HMA, DEMA, …) — clone calcs live next to their
  // base implementations in maBandIndicators.ts.
  ...MA_CLONE_CALCS,
}

/** Multi-figure clones — the factory receives every figure key, in order. */
const MULTI_CALC: Record<string, (keys: string[]) => unknown> = {
  BOLL: bollCalc,
}

const registeredAliases = new Set<string>()

/**
 * Register a uniquely-named klinecharts clone of a duplicable indicator so a
 * second instance can mount on the same pane. No-op when `instanceName` is the
 * built-in base name (the first instance just uses klinecharts' own template)
 * or when the alias is already registered. The clone reuses the catalog figure
 * keys so per-instance style overrides (buildIndicatorStyles) still address it.
 *
 * Every clone calc is wrapped in `withTimeframe`: a passthrough until the
 * instance's extendData carries a `timeframe`, at which point the same math
 * runs over higher-timeframe buckets. This is why an MTF-configured instance
 * must be an alias even when it is the only one of its name — the built-in
 * template's calc cannot be wrapped.
 */
export function ensureInstanceRegistered(instanceName: string, baseName: string): void {
  if (instanceName === baseName) return
  if (registeredAliases.has(instanceName)) return
  const def = getIndicatorDef(baseName)
  const multiFactory = MULTI_CALC[baseName]
  const singleFactory = CALC[baseName]
  if (!def || (!multiFactory && !singleFactory)) return
  const keys = def.figures.map((f) => f.key)
  const calc = multiFactory ? multiFactory(keys) : singleFactory!(keys[0])
  // Sub-pane clones (ATR) must not fold the price OHLC into their own axis —
  // shouldOhlc only makes sense for main-pane overlays.
  const subPane = def.paneType === 'sub'
  registerIndicator({
    name: instanceName,
    shortName: baseName,
    series: 'price' as never,
    precision: 2,
    shouldOhlc: !subPane,
    figures: def.figures.map((f) => ({ key: f.key, title: `${f.label}: `, type: 'line' })),
    calc: withTimeframe(calc as Parameters<typeof withTimeframe>[0]),
  } as IndicatorTemplateParam)
  registeredAliases.add(instanceName)
}
